import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Award,
  BarChart3,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Copy,
  CreditCard,
  DollarSign,
  Flame,
  Gift,
  HelpCircle,
  Home,
  LayoutDashboard,
  Image,
  LogOut,
  FileText,
  Megaphone,
  Settings,
  Shield,
  QrCode,
  Target,
  Trophy,
  User,
  UserPlus,
  Users,
  Percent,
} from 'lucide-react';
import { getThemeColor } from '../config/appConfig';
import BrokerBrand from './common/BrokerBrand';
import { useUser } from '../contexts/UserContext';
import { useKycVerified } from '../hooks/useKycVerified';
import { performLogout } from '../utils/logout';

const THEME = getThemeColor('primary');

const NAV_ICON_MAP = {
  Home,
  LayoutDashboard,
  User,
  Users,
  DollarSign,
  CreditCard,
  Trophy,
  Flame,
  Gift,
  Shield,
  Target,
  Settings,
  HelpCircle,
  FileText,
  BookOpen,
  Award,
  BarChart3,
  Megaphone,
  Image,
  UserPlus,
  Percent,
  QrCode,
};

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearUserData } = useUser();
  const { kycVerified } = useKycVerified();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(new Set());
  // New menu order applies only on the Dashboard 2 page itself.
  const menuMode = location.pathname === '/' ? 'v2' : 'v1';

  useEffect(() => {
    const toggle = () => setIsMobileOpen((v) => !v);
    window.addEventListener('toggle-sidebar', toggle);
    return () => window.removeEventListener('toggle-sidebar', toggle);
  }, []);

  const dashboardV2Items = [
    { id: 'dashboard', path: '/', icon: 'Home', label: 'Dashboard' },
    ...(!kycVerified
      ? [{ id: 'kyc', path: '/kyc', icon: 'Shield', label: 'KYC' }]
      : []),
    {
      id: 'clients',
      icon: 'Users',
      label: 'Clients',
      subMenus: [
        { id: 'clients-listing', path: '/clients', label: 'Client Listing' },
        { id: 'sub-partner-listing', path: '/clients/sub-partners', label: 'Sub Partner Listing' },
      ],
    },
    {
      id: 'earning',
      icon: 'DollarSign',
      label: 'Earning',
      subMenus: [
        { id: 'earning-dashboard', path: '/earnings/dashboard', label: 'Earning Dashboard' },
        { id: 'earning-list', path: '/earnings/list', label: 'Earning List' },
        {
          id: 'earning-sub-commission',
          path: '/earnings/sub-partner-commission',
          label: 'Sub Partner Commission',
        },
      ],
    },
    {
      id: 'payouts',
      icon: 'CreditCard',
      label: 'Payouts',
      subMenus: [
        { id: 'payout-history', path: '/payout-history', label: 'Payout History' },
        { id: 'withdrawal', path: '/withdrawal', label: 'Withdrawal' },
      ],
    },
    {
      id: 'account-tier',
      icon: 'Award',
      label: 'Account Tier',
      subMenus: [
        { id: 'at-progress', path: '/account-tier/progress', label: 'My Progress' },
        { id: 'at-how', path: '/account-tier/how-it-works', label: 'How It Works' },
        {
          id: 'at-instruments',
          path: '/account-tier/instruments-commission',
          label: 'Instruments Commission',
        },
      ],
    },
    {
      id: 'rewards',
      icon: 'Gift',
      label: 'Rewards',
      subMenus: [
        { id: 'rewards-dashboard', path: '/rewards/dashboard', label: 'Dashboard' },
        { id: 'rewards-claim', path: '/rewards/claim', label: 'Claim Rewards' },
        { id: 'rewards-how', path: '/rewards/how-it-works', label: 'How It Works' },
        { id: 'rewards-report', path: '/rewards/report', label: 'Rewards Report' },
        // { id: 'rewards-tiers', path: '/rewards/tiers', label: 'Tiers' },
      ],
    },
    {
      id: 'loyalty-points',
      icon: 'Trophy',
      label: 'Loyalty Points',
      subMenus: [
        { id: 'loyalty-dashboard', path: '/loyalty-points', label: 'Dashboard' },
        { id: 'loyalty-earn', path: '/loyalty/earn', label: 'How You Earn' },
        { id: 'loyalty-history', path: '/loyalty/history', label: 'Loyalty History' },
      ],
    },
    {
      id: 'challenges',
      icon: 'Flame',
      label: 'Challenges',
      subMenus: [
        { id: 'challenges-list', path: '/challenges/list', label: 'Challenges List' },
      ],
    },
    {
      id: 'promotions',
      icon: 'Megaphone',
      label: 'Promotions',
      subMenus: [
        { id: 'promo-list', path: '/promotions/list', label: 'Promotion List' },
        { id: 'promo-my', path: '/promotions/my', label: 'My Promotions' },
      ],
    },
    {
      id: 'refer-a-friend',
      path: '/refer-a-friend',
      icon: 'UserPlus',
      label: 'Refer a Sub Partner',
    },
    {
      id: 'campaigns',
      icon: 'Target',
      label: 'Campaigns',
      subMenus: [
        { id: 'client-campaigns', path: '/campaigns/client', label: 'Client Campaigns' },
        { id: 'sub-affiliate-campaigns', path: '/campaigns/sub-affiliate', label: 'Sub Partner Campaigns' },
      ],
    },
    {
      id: 'banners',
      icon: 'Image',
      label: 'Banners',
      subMenus: [
        { id: 'partner-banners', path: '/marketing/banners', label: 'Partner Banners' },
        {
          id: 'sub-partner-banners',
          path: '/marketing/sub-affiliate-banners',
          label: 'Sub Partner Banners',
        },
      ],
    },
    {
      id: 'qr-code',
      icon: 'QrCode',
      label: 'QR Code',
      subMenus: [
        { id: 'qr-client', path: '/qr-code/client', label: 'Client QR' },
        { id: 'qr-partner', path: '/qr-code/partner', label: 'Partner QR' },
      ],
    },
    {
      id: 'education',
      icon: 'BookOpen',
      label: 'Education',
      subMenus: [
        { id: 'education-courses', path: '/education/courses', label: 'Courses' },
        { id: 'education-analytics', path: '/education/analytics', label: 'Analytics' },
      ],
    },
  ];

  // Original menu order is kept on every page except Dashboard 2.
  const ORIGINAL_ORDER = [
    'dashboard', 'account-tier', 'kyc', 'clients', 'earning', 'payouts',
    'loyalty-points', 'rewards', 'refer-a-friend', 'qr-code', 'campaigns', 'banners',
    'education', 'promotions', 'challenges',
  ];
  const menuItems = menuMode === 'v2'
    ? dashboardV2Items
    : ORIGINAL_ORDER.map((id) => dashboardV2Items.find((i) => i.id === id)).filter(Boolean);

  const bottomItems = [
    {
      id: 'reports',
      icon: 'FileText',
      label: 'Reports',
      subMenus: [
        { id: 'commission-report', path: '/reports/commission', label: 'Commission Report' },
        { id: 'client-report', path: '/reports/clients', label: 'Client Report' },
      ],
    },
    {
      id: 'settings',
      icon: 'Settings',
      label: 'Settings',
      subMenus: [
        { id: 'withdrawal-details', path: '/settings/withdrawal-details', label: 'Withdrawal Details' },
        { id: 'security', path: '/settings/security', label: 'Security' },
      ],
    },
    { id: 'support', path: '/support', icon: 'HelpCircle', label: 'Support' },
  ];

  const allItems = [...menuItems, ...bottomItems];

  const findActiveParentId = (pathname) => {
    const activeParent = allItems.find((item) =>
      item.subMenus?.some(
        (sub) =>
          pathname === sub.path ||
          (sub.path !== '/' && pathname.startsWith(`${sub.path}/`))
      )
    );
    return activeParent?.id ?? null;
  };

  useEffect(() => {
    const activeParentId = findActiveParentId(location.pathname);
    setExpandedMenus(activeParentId ? new Set([activeParentId]) : new Set());
    // findActiveParentId is stable for pathname-driven expand state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const renderItem = (item) => {
    const Icon = NAV_ICON_MAP[item.icon] || Home;
    const hasSubMenus = item.subMenus?.length > 0;
    const isExpanded = expandedMenus.has(item.id);
    const isActive =
      item.path &&
      (location.pathname === item.path ||
        (item.path !== '/' && location.pathname.startsWith(`${item.path}/`)));
    const hasActiveSub = hasSubMenus && item.subMenus.some((s) => location.pathname === s.path);
    const active =
      (Boolean(isActive) && !hasActiveSub) ||
      (item.id === 'dashboard' && location.pathname === '/');

    return (
      <div key={item.id} className="mb-0.5">
        <button
          type="button"
          onClick={() => {
            setIsMobileOpen(false);
            if (hasSubMenus) {
              setExpandedMenus((prev) => {
                if (prev.has(item.id)) return new Set();
                return new Set([item.id]);
              });
            } else if (item.path) {
              localStorage.setItem('currentRoute', item.path);
              navigate(item.path);
            }
          }}
          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
            active ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
          }`}
          style={active ? { backgroundColor: THEME } : undefined}
        >
          <span className="flex items-center gap-3">
            <Icon className="h-5 w-5 shrink-0" />
            {item.label}
          </span>
          {hasSubMenus && (isExpanded ? <ChevronDown className="w-4 h-4 opacity-60" /> : <ChevronRight className="w-4 h-4 opacity-60" />)}
        </button>
        {hasSubMenus && isExpanded && (
          <div className="ml-9 mt-1 space-y-0.5 border-l-2 border-gray-100 pl-3">
            {item.subMenus.map((sub) => {
              const subActive = location.pathname === sub.path;
              return (
                <button
                  key={sub.id}
                  type="button"
                  onClick={() => {
                    setIsMobileOpen(false);
                    setExpandedMenus(new Set([item.id]));
                    navigate(sub.path);
                  }}
                  className={`block w-full text-left py-1.5 text-xs rounded-lg px-2 transition ${
                    subActive ? 'font-semibold text-gray-900 bg-gray-100' : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {sub.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const partnerCode = localStorage.getItem('affiliateCode') || '';

  const handleLogout = () => {
    setIsMobileOpen(false);
    performLogout(clearUserData);
    navigate('/login');
  };

  return (
    <>
      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setIsMobileOpen(false)} />
      )}

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col w-[260px] shrink-0 bg-white border-r border-gray-200 transition-transform duration-300 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="px-5 py-5 border-b border-gray-100">
          <BrokerBrand isDarkMode={false} size="medium" showIcon showName />
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
          {menuItems.map(renderItem)}
          <div className="my-3 border-t border-gray-100" />
          {bottomItems.map(renderItem)}
          <div className="mt-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-700 transition"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              Logout
            </button>
          </div>
        </nav>

        <div className="m-3 mb-4 p-4 rounded-2xl border border-gray-200 bg-white shadow-sm">
          <p className="text-xs text-gray-500 mb-3">Your Partner Code</p>
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="text-xl font-bold tracking-wide" style={{ color: THEME }}>
              {partnerCode}
            </span>
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(partnerCode)}
              className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition shrink-0"
              aria-label="Copy code"
            >
              <Copy className="h-4 w-4 text-gray-500" />
            </button>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed mb-4">
            Share your link and start earning more.
          </p>
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(partnerCode)}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-90 transition"
            style={{ backgroundColor: THEME }}
          >
            <Gift className="h-4 w-4 text-white" strokeWidth={2} />
            Share Now
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
